import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';

import { Home, Feed, Publish, Profile, Listing, Faqs, ChangePassword, Discussion } from "./containers";
import { Navbar, Login, Message, Ride } from "./components";
import SplashScreen from "./components/splashScreen/SplashScreen";
import { connectSocket, disconnectSocket } from "./services/socket";
import { initCapacitor, setupBackButton, onDeepLink } from "./services/capacitor";
import { registerPush, setupPushTapHandler, unregisterPush, getCurrentToken } from "./services/pushNotifications";
import { Capacitor } from "@capacitor/core";
import useNetworkStatus from "./hooks/useNetworkStatus";
import OfflineBanner from "./components/offlineBanner/OfflineBanner";
import ErrorBoundary from "./components/errorBoundary/ErrorBoundary";
import NotFound from "./components/notFound/NotFound";
import ForgotPassword from "./components/forgotPassword/ForgotPassword";
import ResetPassword from "./components/resetPassword/ResetPassword";
import Terms from "./containers/terms/Terms";
import Privacy from "./containers/privacy/Privacy";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import './App.css';

// Lazy-loaded routes (heavy dependencies: Stripe, admin, receipt)
const PaymentPage = React.lazy(() => import("./components/payment/PaymentPage"));
const PaymentSuccess = React.lazy(() => import("./components/payment/PaymentSuccess"));
const PaymentFailed = React.lazy(() => import("./components/payment/PaymentFailed"));
const Receipt = React.lazy(() => import("./components/receipt/Receipt"));
const Admin = React.lazy(() => import("./components/admin/Admin"));

const App = () => {
  const location = useLocation(); // Get the current location

  // Scroll to top whenever the location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]); // Dependency array includes location

  const [loginUser, setLoginUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('loginUser');
      return storedUser && storedUser !== "undefined" ? JSON.parse(storedUser) : {}; // Ensure it's parsed correctly
    } catch (e) {
      console.error("Failed to parse loginUser from localStorage", e);
      return {}; // Default to an empty object in case of error
    }
  });

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const storedUser = localStorage.getItem('loginUser');
    return storedUser && Object.keys(JSON.parse(storedUser || "{}")).length > 0;
  });

  const navigate = useNavigate();

  const handleLogin = (user) => {
    if (user && typeof user === 'object' && Object.keys(user).length > 0) {
      setLoginUser(user);
      localStorage.setItem('loginUser', JSON.stringify(user));
      connectSocket();
      registerPush();
    }
  };

  // Function to handle logout
  const handleLogout = () => {
    // Remove device push token (fire-and-forget)
    const token = getCurrentToken();
    if (Capacitor.isNativePlatform() && token) {
      axios.delete("/api/v1/user/device-token", { data: { token } }).catch(() => {});
    }
    unregisterPush();
    disconnectSocket();
    setLoginUser({});
    localStorage.removeItem('loginUser');
  };

  // Watch for changes in loginUser to update isLoggedIn
  useEffect(() => {
    setIsLoggedIn(loginUser && Object.keys(loginUser).length > 0);
  }, [loginUser]);

  // Connect socket and register push on mount if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      connectSocket();
      registerPush();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up push notification tap handler
  useEffect(() => {
    setupPushTapHandler(navigate);
  }, [navigate]);

  // Listen to changes in localStorage from other tabs or actions
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'loginUser') {
        const storedUser = localStorage.getItem('loginUser');

        if (storedUser === "undefined") {
          localStorage.removeItem('loginUser');
          setLoginUser({});
        }
        else
          setLoginUser(storedUser ? JSON.parse(storedUser) : {});
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const isOnline = useNetworkStatus();
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem("splashShown"));
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    sessionStorage.setItem("splashShown", "1");
  }, []);

  // Initialize Capacitor native features
  useEffect(() => {
    initCapacitor();
  }, []);

  // Android back button handler
  useEffect(() => {
    const listener = setupBackButton(
      () => window.history.length > 1,
      () => window.history.back()
    );
    return () => {
      if (listener) listener.then((h) => h.remove());
    };
  }, []);

  // Deep link handler (OAuth callbacks + universal links)
  useEffect(() => {
    const cleanup = onDeepLink((url) => {
      try {
        const parsed = new URL(url);

        // Handle OAuth callback: com.sastiride.app://auth?user=...
        if (parsed.hostname === "auth" || parsed.pathname === "//auth") {
          const userString = parsed.searchParams.get("user");
          const error = parsed.searchParams.get("error");
          if (error) { console.error("[DeepLink] OAuth error:", error); return; }
          if (userString) {
            const user = JSON.parse(decodeURIComponent(userString));
            handleLogin(user);
          }
          return;
        }

        // Handle app URLs: https://sastiride.com/ride/abc123 or https://kamracarpool.vercel.app/ride/abc123
        const path = parsed.pathname;
        if (path && path !== "/") {
          window.history.pushState({}, "", path);
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      } catch (e) {
        console.error("[DeepLink] Error parsing URL:", e);
      }
    });
    return cleanup;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <Toaster position="top-center" toastOptions={{ duration: 4000 }} />
      {!isOnline && <OfflineBanner />}
      <Navbar isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} ></Navbar>
      <ErrorBoundary>
        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
        <Routes className='App'>
          <Route path="/" element={<Feed isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/about" element={<Home isLoggedIn={isLoggedIn} />}/>
          <Route path="/feed" element={<Navigate to="/" replace />}/>
          <Route path="/login" element={<Login isLoggedIn={isLoggedIn} onLogin={handleLogin} />}/>
          <Route path="/message" element={<Message />}/>
          <Route path="/forgot-password" element={<ForgotPassword />}/>
          <Route path="/reset-password/:token" element={<ResetPassword />}/>
          <Route path="/publish" element={<Publish isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/profile/:profileId?" element={<Profile isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/change-password" element={<ChangePassword isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/listing/:profileId?" element={<Listing isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/ride/:rideId" element={<Ride isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/admin" element={<Admin isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/community" element={<Discussion isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/faqs" element={<Faqs />}/>
          <Route path="/pay/:rideId" element={<PaymentPage isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/payment/success/:paymentId" element={<PaymentSuccess />}/>
          <Route path="/payment/failed/:paymentId?" element={<PaymentFailed />}/>
          <Route path="/receipt/:paymentId" element={<Receipt isLoggedIn={isLoggedIn} loginUser={loginUser} onLogout={handleLogout} />}/>
          <Route path="/terms" element={<Terms />}/>
          <Route path="/privacy" element={<Privacy />}/>
          <Route path="*" element={<NotFound />}/>
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

// Wrap the App component in BrowserRouter
const WrappedApp = () => {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
};

export default WrappedApp;
